import type { MoneyCents } from '../core/types';

export const DEFAULT_AUTO_STOP_THRESHOLD_X = 50;

export interface AutoSpinDecision {
  readonly isSafeToStart: boolean;
  readonly isFreeSpinMode: boolean;
  /** Fixed challenge modes can opt out of the optional base-win stop policy. */
  readonly allowWinStop?: boolean;
  readonly lastWinCents: MoneyCents;
  readonly betCents: MoneyCents;
}

/**
 * Owns the user's auto-spin intent. It deliberately has no timer and cannot
 * initiate a spin itself, which keeps scheduling and double-spin guards in the
 * game controller.
 */
export class AutoSpinController {
  private enabled = false;
  private stopEnabled = false;
  private stopThresholdX = DEFAULT_AUTO_STOP_THRESHOLD_X;

  get isEnabled(): boolean {
    return this.enabled;
  }

  requestEnable(isSafeToStart: boolean): boolean {
    if (!isSafeToStart) return false;
    this.enabled = true;
    return true;
  }

  stop(): void {
    this.enabled = false;
  }

  toggle(isSafeToStart: boolean): boolean {
    if (this.enabled) {
      this.stop();
      return false;
    }
    this.requestEnable(isSafeToStart);
    return this.enabled;
  }

  configureStop(enabled: boolean, thresholdX: number): void {
    if (!Number.isFinite(thresholdX) || thresholdX < 1 || thresholdX > 100_000) {
      throw new RangeError('Die Auto-Stop-Schwelle muss zwischen 1× und 100.000× liegen.');
    }
    this.stopEnabled = enabled;
    this.stopThresholdX = thresholdX;
  }

  shouldQueueNext(decision: AutoSpinDecision): boolean {
    if (!this.enabled || !decision.isSafeToStart) return false;

    if (
      this.stopEnabled
      && decision.allowWinStop !== false
      && !decision.isFreeSpinMode
      && decision.betCents > 0
      && decision.lastWinCents / decision.betCents >= this.stopThresholdX
    ) {
      this.stop();
      return false;
    }

    return true;
  }
}
